import React, { useState, useContext } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { ToastContext } from './ToastProvider';

const CodRemittanceModal = ({ isOpen, onClose, delivery, onRemittanceConfirmed }) => {
  const [remittanceAmount, setRemittanceAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { notify } = useContext(ToastContext);

  // Calculate remaining amount
  const codAmount = Number(delivery?.codAmount || 0);
  const totalRemitted = Number(delivery?.totalRemitted || 0);
  const remainingAmount = codAmount - totalRemitted;
  const remittanceCount = delivery?.remittanceCount || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!remittanceAmount || Number(remittanceAmount) <= 0) {
      notify('Please enter a valid remittance amount', { type: 'error' });
      return;
    }

    if (Number(remittanceAmount) > remainingAmount) {
      notify('Remittance amount cannot exceed remaining COD amount', { type: 'error' });
      return;
    }

    if (remittanceCount >= 3) {
      notify('Maximum 3 remittances allowed per delivery', { type: 'error' });
      return;
    }

      const requestData = {
        deliveryId: delivery.deliveryId, // Use deliveryId from payload
        remittanceAmount: Number(remittanceAmount),
        notes: notes.trim()
      };

    setIsProcessing(true);
    try {
      const confirmRemittance = httpsCallable(functions, 'confirmCodRemittance');
      const result = await confirmRemittance(requestData);

      if (result.data?.ok) {
        const { isCompleted, newStatus } = result.data;
        
        if (isCompleted) {
          notify(`Delivery completed! Rider payment credited to wallet.`, { type: 'success' });
        } else {
          notify(`Remittance confirmed. ${3 - (remittanceCount + 1)} remittances remaining.`, { type: 'success' });
        }

        onRemittanceConfirmed(result.data);
        onClose();
      }
    } catch (error) {
      console.error('Error confirming remittance:', error);
      notify(error?.message || 'Failed to confirm remittance', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setRemittanceAmount('');
      setNotes('');
      onClose();
    }
  };

  if (!isOpen || !delivery) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200] pointer-events-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 relative pointer-events-auto" style={{ position: 'relative', zIndex: 1201 }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Confirm COD Remittance</h2>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Delivery Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm text-gray-600">Order ID</div>
            <div className="font-medium">{delivery.orderId || delivery.id}</div>
            
            <div className="mt-2 text-sm text-gray-600">Customer</div>
            <div className="font-medium">{delivery.buyerName || 'Unknown'}</div>
            
            <div className="mt-2 text-sm text-gray-600">Total COD Amount</div>
            <div className="font-medium text-lg">₱{codAmount.toFixed(2)}</div>
          </div>

          {/* Remittance Progress */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-sm text-blue-600 font-medium mb-2">Remittance Progress</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total Remitted:</span>
                <span className="font-medium">₱{totalRemitted.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Remaining:</span>
                <span className="font-medium">₱{remainingAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Remittances:</span>
                <span className="font-medium">{remittanceCount}/3</span>
              </div>
            </div>
          </div>

          {/* Remittance Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="remittanceAmount" className="block text-sm font-medium text-gray-700 mb-1">
                Remittance Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₱</span>
                <input
                  type="number"
                  id="remittanceAmount"
                  value={remittanceAmount}
                  onChange={(e) => setRemittanceAmount(e.target.value)}
                  step="0.01"
                  min="0.01"
                  max={remainingAmount}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                  disabled={isProcessing}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max: ₱{remainingAmount.toFixed(2)}
              </p>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Any additional notes about this remittance..."
                disabled={isProcessing}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing || !remittanceAmount || Number(remittanceAmount) <= 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : 'Confirm Remittance'}
              </button>
            </div>
          </form>

          {/* Warning Messages */}
          {remittanceCount >= 2 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="text-sm text-yellow-800">
                <strong>Warning:</strong> This is remittance #{remittanceCount + 1}. 
                {remittanceCount + 1 >= 3 ? ' Delivery will be marked complete after this.' : ' Only 1 more remittance allowed.'}
              </div>
            </div>
          )}

          {remainingAmount <= 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-sm text-green-800">
                <strong>Complete:</strong> Full COD amount has been remitted. 
                Delivery can be marked complete.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodRemittanceModal;
